import {
  Scene,
  StandardMaterial,
  Texture,
  Mesh,
  PBRMaterial,
  AssetsManager,
} from 'babylonjs'
import {
  IMockSkyBox,
  IEnvTextureTask,
  IEnvTexture,
  ISkyBoxPreload,
} from '../../type/skyboxType'
import { Context3D } from '../3d-context'
import { customLoadingUI, customLoadingUIText } from '../commonLib/engine'
import { loadEnvTexture, loadEnvTextureAssetTask } from './envTexture'
import { loadHdrTextureAssetTask } from './hdrTexture'
import { loadImageLayer } from './imageLayer'

export enum EnvType {
  NONE,
  ENV,
  DDS,
  HDR,
  IMAGE,
}

export const getTypeByFileName = (fileName: string): EnvType | null => {
  const fileNameParts = fileName.split('.')
  const ext =
    fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1] : null

  const typeStringArray = Object.values(EnvType).map(r => r.toString())
  const extUpper = ext && ext.trim().toUpperCase()

  if (extUpper) {
    if (typeStringArray.includes(extUpper)) {
      return EnvType[extUpper as keyof typeof EnvType] as EnvType
    } else if (['JPG', 'PNG'].includes(extUpper)) {
      return EnvType.IMAGE
    }
  }

  return null
}

export const setDefaultEnvironment = (context = Context3D.get()) => {
  const scene = context.scene as Scene
  scene.environmentTexture = loadEnvTexture(
    'https://brikl-poc.s3.ap-southeast-1.amazonaws.com/babylonjs/assets/env/studio.env',
    scene
  )
  // you can call setUpEnvironment() here
}

export const setUpEnvironment = (
  env: IMockSkyBox,
  context = Context3D.get(),
  setGlobalEnvironment = true
): void => {
  const scene = context.scene as Scene
  const backgroundSkybox = scene.getMeshByName('BackgroundSkybox')
  if (backgroundSkybox) {
    backgroundSkybox.dispose(false, true)
  }
  const backgroundHelper = scene.getMeshByName('BackgroundHelper')
  if (backgroundHelper) {
    backgroundHelper.dispose(false, true)
  }
  const hdrSkyBox = scene.getMeshByName('hdrSkyBox')
  if (hdrSkyBox) {
    hdrSkyBox.dispose(false, true)
  }

  const bgImage = scene.layers?.find(r => r.name === 'BackgroudImage')
  if (bgImage) {
    bgImage.dispose()
  }

  const pathFile = getEnvPathByPerformance(env)

  const type = getTypeByFileName(pathFile)

  if (type === EnvType.IMAGE) {
    loadImageLayer(pathFile, scene)
    setDefaultEnvironment()
  } else {
    var assetsManager = new AssetsManager(scene)

    if (type === EnvType.HDR) {
      setTextureByTask(
        scene,
        [loadHdrTextureAssetTask(pathFile, assetsManager)],
        null,
        setGlobalEnvironment
      )
    } else {
      setTextureByTask(
        scene,
        [loadEnvTextureAssetTask(pathFile, assetsManager)],
        null,
        setGlobalEnvironment
      )
    }

    customLoadingUIText('Loading environment')
    assetsManager.load()
    customLoadingUI()
  }
}

const setTextureByTask = (
  scene: Scene,
  textureTasks: IEnvTextureTask[],
  lightsTexture: IEnvTexture | null = null,
  setGlobalEnvironment = true
) => {
  textureTasks.forEach((textureTask, index) => {
    textureTask.runTask(
      scene,
      () => {
        customLoadingUIText('')
        if (index !== 0) {
          const skybox = scene.getMeshByName('hdrSkyBox') as Mesh
          const skyboxMaterial = new StandardMaterial('skyBox', scene)
          skyboxMaterial.backFaceCulling = false
          skyboxMaterial.reflectionTexture = textureTask.texture.clone()
          if (skyboxMaterial.reflectionTexture) {
            skyboxMaterial.reflectionTexture.coordinatesMode =
              Texture.SKYBOX_MODE
          }
          skyboxMaterial.disableLighting = true
          skybox.material = skyboxMaterial
          ;(skybox?.material as PBRMaterial).microSurface = 0.95 // blur = 0.9 - 0.95
        } else {
          const skybox = scene.createDefaultSkybox(
            textureTask.texture,
            true,
            1024,
            0,
            setGlobalEnvironment
          ) as Mesh
          ;(skybox?.material as PBRMaterial).microSurface = 0.95 // blur = 0.9 - 0.95
        }
      },
      () => {}
    )
  })
}

const getEnvPathByPerformance = (env: IMockSkyBox) => {
  //TODO fileter path file by machine performance
  return env.path
}

export const preloadEnvs = async (
  skyboxes: IMockSkyBox[],
  scene: Scene
): Promise<ISkyBoxPreload[]> => {
  console.log('preload skyboxes')
  var assetsManager = new AssetsManager(scene)
  const promises: Promise<ISkyBoxPreload>[] = []

  skyboxes.forEach(env => {
    const path = getEnvPathByPerformance(env)
    const type = getTypeByFileName(path)

    if (type === EnvType.HDR) {
      const task = loadHdrTextureAssetTask(path, assetsManager)

      promises.push(
        new Promise((resolve, reject) => {
          task.onSuccess = task => {
            console.log('success', path)
            resolve({
              id: env.id,
              texture: task.texture.clone(),
            })
          }
          task.onError = (task, message) => {
            reject(null)
          }
        })
      )
    } else {
      const task = loadEnvTextureAssetTask(path, assetsManager)
      promises.push(
        new Promise((resolve, reject) => {
          task.onSuccess = task => {
            console.log('success', path)
            resolve({
              id: env.id,
              texture: task.texture.clone(),
            })
          }
          task.onError = (task, message) => {
            reject(null)
          }
        })
      )
    }
  })

  const all = Promise.all(promises)

  customLoadingUIText('Preload environments')
  await assetsManager.loadAsync()

  return await all
}
